import React, { useCallback, useEffect, useState } from "react";
import { Card } from "../ui/card";
import { Receipt, Plus, FileDown, Mail, Link2, Check, BadgeDollarSign, Loader2, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const errTxt = (e) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Something went wrong");
const STATUS_STYLE = {
  draft: "border-slate-500/50 text-slate-400",
  sent: "border-cyan-500/50 text-cyan-300",
  paid: "border-emerald-500/50 text-emerald-400",
  overdue: "border-red-500/50 text-red-400",
};

export const TcInvoices = ({ clients, jobs, reloadAll }) => {
  const [invoices, setInvoices] = useState([]);
  const [open, setOpen] = useState(false);
  const [clientId, setClientId] = useState("");
  const [sel, setSel] = useState([]);
  const [custom, setCustom] = useState({ desc: "", amount: "" });
  const [emailFor, setEmailFor] = useState(null);
  const [emailForm, setEmailForm] = useState({ to_email: "", message: "" });
  const [editFor, setEditFor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reviewUrl, setReviewUrl] = useState("");
  const [reviewReqs, setReviewReqs] = useState([]);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/truck-cleaning/invoices"); setInvoices(data.invoices); } catch (_) {}
    try {
      const [{ data: s }, { data: r }] = await Promise.all([
        api.get("/truck-cleaning/settings"), api.get("/truck-cleaning/review-requests")]);
      setReviewUrl(s.settings?.google_review_url || "");
      setReviewReqs(r.requests || []);
    } catch (_) {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveReviewUrl = async () => {
    try {
      await api.put("/truck-cleaning/settings", { google_review_url: reviewUrl });
      toast.success("Review link saved — texted automatically after every paid invoice");
    } catch (e2) { toast.error(errTxt(e2)); }
  };

  const billableJobs = jobs.filter((j) => j.client_id === clientId && j.status !== "paid");

  const create = async () => {
    const custom_items = custom.desc && Number(custom.amount) > 0 ? [{ desc: custom.desc, amount: Number(custom.amount) }] : [];
    if (sel.length === 0 && custom_items.length === 0) { toast.error("Select at least one job or add a custom line"); return; }
    setBusy(true);
    try {
      await api.post("/truck-cleaning/invoices", { client_id: clientId, job_ids: sel, custom_items, due_days: 15 });
      toast.success("Invoice created");
      setOpen(false); setSel([]); setCustom({ desc: "", amount: "" });
      load();
    } catch (e2) { toast.error(errTxt(e2)); }
    finally { setBusy(false); }
  };

  const pdf = async (inv) => {
    try {
      const r = await api.get(`/truck-cleaning/invoices/${inv.invoice_id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `Orisei_${inv.invoice_id}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch (_) { toast.error("PDF failed"); }
  };

  const copyPayLink = (inv) => {
    const url = `${window.location.origin}/tc/invoice/${inv.invoice_id}`;
    navigator.clipboard?.writeText(url).then(() => toast.success("Payment link copied")).catch(() => toast.info(url));
  };

  const markPaid = async (inv) => {
    try { await api.post(`/truck-cleaning/invoices/${inv.invoice_id}/mark-paid`); toast.success("Marked paid"); load(); reloadAll(); }
    catch (e2) { toast.error(errTxt(e2)); }
  };

  const startEdit = (inv) => setEditFor({
    invoice_id: inv.invoice_id, company: inv.company,
    line_items: (inv.line_items || []).map((li) => ({ ...li })),
    due_date: (inv.due_date || "").slice(0, 10), notes: inv.notes || "",
  });
  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.put(`/truck-cleaning/invoices/${editFor.invoice_id}`, {
        line_items: editFor.line_items,
        due_date: editFor.due_date ? `${editFor.due_date}T23:59:59+00:00` : "",
        notes: editFor.notes,
      });
      toast.success("Invoice updated");
      setEditFor(null); load(); reloadAll();
    } catch (e2) { toast.error(errTxt(e2)); }
    finally { setBusy(false); }
  };

  const sendEmail = async () => {
    setBusy(true);
    try {
      await api.post(`/truck-cleaning/invoices/${emailFor.invoice_id}/email`, emailForm);
      toast.success("Invoice emailed with pay link");
      setEmailFor(null); load();
    } catch (e2) { toast.error(errTxt(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="tc-invoices">
      <Card className="p-4 bg-slate-950/70 border-cyan-500/20" data-testid="tc-review-engine">
        <div className="text-xs font-mono uppercase tracking-widest text-cyan-300 mb-1">Review Engine — automatic after every paid invoice</div>
        <div className="text-[10px] text-slate-500 mb-2">The moment an invoice is paid (Stripe or marked paid), the client gets a thank-you text with your Google review link.</div>
        <div className="flex flex-wrap items-center gap-2">
          <input value={reviewUrl} onChange={(e) => setReviewUrl(e.target.value)} placeholder="Your Google review link — e.g. https://g.page/r/XXXX/review"
                 className="h-9 px-3 rounded-full bg-[#11151F] border border-white/10 text-xs text-white flex-1 min-w-[260px] outline-none focus:border-cyan-400" data-testid="tc-review-url-input" />
          <button onClick={saveReviewUrl} className="h-9 px-4 rounded-full bg-cyan-500 text-black text-xs font-black" data-testid="tc-review-url-save">SAVE LINK</button>
        </div>
        {reviewReqs.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {reviewReqs.slice(0, 6).map((r) => (
              <span key={r.invoice_id} className="px-2.5 py-1 rounded-full border border-white/10 text-[9px] font-mono text-slate-400" data-testid={`tc-review-req-${r.invoice_id}`}>
                {r.company || r.invoice_id} · {r.status === "sms_queued" ? "review text queued" : r.status}
              </span>
            ))}
          </div>
        )}
      </Card>
      <div className="flex justify-end">
        <button onClick={() => setOpen(!open)} data-testid="tc-invoice-new-btn"
                className="px-4 py-2 rounded-full bg-amber-500 text-black font-bold text-xs inline-flex items-center gap-1.5"><Plus size={13} /> New Invoice</button>
      </div>
      {open && (
        <Card className="p-4 bg-slate-950/70 border-amber-500/30" data-testid="tc-invoice-form">
          <div className="flex flex-wrap gap-2 items-center mb-3">
            <Receipt className="text-amber-400" size={16} />
            <select value={clientId} onChange={(e) => { setClientId(e.target.value); setSel([]); }} data-testid="tc-invoice-client-select"
                    className="h-9 rounded-lg bg-slate-950 border border-white/15 px-2 text-xs min-w-[200px]">
              <option value="">Select client…</option>
              {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.company}</option>)}
            </select>
          </div>
          {clientId && (
            <div className="space-y-1.5 mb-3">
              {billableJobs.length === 0 && <div className="text-[11px] text-slate-500 font-mono">No unbilled jobs for this client — add a custom line below.</div>}
              {billableJobs.map((j) => (
                <label key={j.job_id} className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer" data-testid={`tc-invoice-job-${j.job_id}`}>
                  <input type="checkbox" checked={sel.includes(j.job_id)}
                         onChange={() => setSel((s) => s.includes(j.job_id) ? s.filter((x) => x !== j.job_id) : [...s, j.job_id])}
                         className="accent-amber-500" />
                  <span className="font-mono text-amber-300">{j.job_id}</span> {j.date} · {j.cabs} cabs · <span className="font-bold">${j.price.toLocaleString()}</span>
                  <span className="text-[10px] text-slate-600 uppercase">({j.status})</span>
                </label>
              ))}
              <div className="flex gap-2 pt-2">
                <input value={custom.desc} onChange={(e) => setCustom({ ...custom, desc: e.target.value })} placeholder="Custom line (optional)"
                       data-testid="tc-invoice-custom-desc" className="h-9 rounded-lg bg-slate-950 border border-white/15 px-2.5 text-xs flex-1" />
                <input value={custom.amount} onChange={(e) => setCustom({ ...custom, amount: e.target.value })} placeholder="$" type="number"
                       data-testid="tc-invoice-custom-amount" className="h-9 w-24 rounded-lg bg-slate-950 border border-white/15 px-2.5 text-xs" />
              </div>
              <div className="flex justify-between items-center pt-2">
                <div className="text-xs font-mono text-slate-400">
                  Total: <span className="text-amber-300 font-bold">
                    ${(billableJobs.filter((j) => sel.includes(j.job_id)).reduce((s, j) => s + j.price, 0) + (Number(custom.amount) || 0)).toLocaleString()}
                  </span> · Net 15
                </div>
                <button onClick={create} disabled={busy} data-testid="tc-invoice-create-btn"
                        className="px-5 py-2 rounded-full bg-amber-500 text-black font-bold text-xs disabled:opacity-60">
                  {busy ? "Creating…" : "Create Invoice"}
                </button>
              </div>
            </div>
          )}
        </Card>
      )}
      <Card className="bg-slate-950/70 border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[10px] font-mono uppercase text-slate-500 border-b border-white/5">
            <th className="p-3">Invoice</th><th className="p-3">Client</th><th className="p-3">Total</th><th className="p-3">Due</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr></thead>
          <tbody>
            {invoices.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-600 text-xs font-mono">No invoices yet — create one from completed jobs.</td></tr>}
            {invoices.map((inv) => (
              <tr key={inv.invoice_id} className="border-b border-white/5" data-testid={`tc-invoice-row-${inv.invoice_id}`}>
                <td className="p-3 font-mono text-[11px] text-amber-300">{inv.invoice_id}<div className="text-slate-600">{(inv.created_at || "").slice(0, 10)}</div></td>
                <td className="p-3 text-xs text-slate-200">{inv.company}</td>
                <td className="p-3 tabular-nums font-bold">${Number(inv.total).toLocaleString()}</td>
                <td className="p-3 text-[11px] text-slate-500">{(inv.due_date || "").slice(0, 10)}</td>
                <td className="p-3"><span className={`px-2 py-0.5 rounded-full border text-[10px] font-mono uppercase ${STATUS_STYLE[inv.status] || ""}`}>{inv.status}</span></td>
                <td className="p-3">
                  <div className="flex gap-2 items-center">
                    <button onClick={() => pdf(inv)} title="Download branded PDF" data-testid={`tc-invoice-pdf-${inv.invoice_id}`} className="text-amber-400 hover:text-amber-300"><FileDown size={14} /></button>
                    <button onClick={() => copyPayLink(inv)} title="Copy payment link" data-testid={`tc-invoice-paylink-${inv.invoice_id}`} className="text-cyan-300 hover:text-cyan-200"><Link2 size={14} /></button>
                    <button onClick={() => { setEmailFor(inv); setEmailForm({ to_email: inv.email || "", message: "" }); }} title="Email invoice"
                            data-testid={`tc-invoice-email-${inv.invoice_id}`} className="text-slate-400 hover:text-white"><Mail size={14} /></button>
                    {inv.status !== "paid" && (
                      <button onClick={() => startEdit(inv)} title="Edit invoice" data-testid={`tc-invoice-edit-${inv.invoice_id}`}
                              className="text-slate-400 hover:text-amber-300"><Pencil size={14} /></button>
                    )}
                    {inv.status !== "paid" && (
                      <button onClick={() => markPaid(inv)} title="Mark paid" data-testid={`tc-invoice-markpaid-${inv.invoice_id}`}
                              className="text-emerald-500 hover:text-emerald-400"><BadgeDollarSign size={14} /></button>
                    )}
                    {inv.status === "paid" && <Check size={14} className="text-emerald-400" />}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {emailFor && (
        <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4" onClick={() => setEmailFor(null)}>
          <Card className="w-full max-w-md p-5 bg-slate-950 border-amber-500/30" onClick={(e) => e.stopPropagation()} data-testid="tc-invoice-email-dialog">
            <div className="font-black text-white mb-1">Email {emailFor.invoice_id}</div>
            <p className="text-[11px] text-slate-500 mb-3">Branded invoice PDF attached + secure pay-online link. Requires Resend key in Connections.</p>
            <input value={emailForm.to_email} onChange={(e) => setEmailForm({ ...emailForm, to_email: e.target.value })} placeholder="client@fleet.com"
                   data-testid="tc-invoice-email-to" className="w-full h-10 rounded-lg bg-slate-900 border border-white/15 px-3 text-sm mb-2 outline-none focus:border-amber-400" />
            <textarea value={emailForm.message} onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })} placeholder="Personal note (optional)" rows={3}
                      data-testid="tc-invoice-email-message" className="w-full rounded-lg bg-slate-900 border border-white/15 px-3 py-2 text-sm mb-3 outline-none focus:border-amber-400" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setEmailFor(null)} className="px-4 py-2 rounded-full border border-white/15 text-slate-300 text-xs font-bold">Cancel</button>
              <button onClick={sendEmail} disabled={busy || !emailForm.to_email} data-testid="tc-invoice-email-send"
                      className="px-5 py-2 rounded-full bg-amber-500 text-black font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-60">
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />} Send
              </button>
            </div>
          </Card>
        </div>
      )}
      {editFor && (
        <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4" onClick={() => setEditFor(null)}>
          <Card className="w-full max-w-lg p-5 bg-slate-950 border-amber-500/30" onClick={(e) => e.stopPropagation()} data-testid="tc-invoice-edit-dialog">
            <div className="font-black text-white mb-1">Edit {editFor.invoice_id} <span className="text-slate-500 font-normal text-xs">· {editFor.company}</span></div>
            <p className="text-[11px] text-slate-500 mb-3">Change line items, amounts, due date and notes — the branded PDF and pay link update instantly.</p>
            <div className="space-y-2 mb-3 max-h-[240px] overflow-y-auto">
              {editFor.line_items.map((li, i) => (
                <div key={i} className="flex gap-2">
                  <input value={li.desc} onChange={(e) => { const items = [...editFor.line_items]; items[i] = { ...li, desc: e.target.value }; setEditFor({ ...editFor, line_items: items }); }}
                         data-testid={`tc-invoice-edit-desc-${i}`} className="flex-1 h-9 rounded-lg bg-slate-900 border border-white/15 px-2.5 text-xs outline-none focus:border-amber-400" />
                  <input type="number" step="0.01" value={li.amount} onChange={(e) => { const items = [...editFor.line_items]; items[i] = { ...li, amount: Number(e.target.value) }; setEditFor({ ...editFor, line_items: items }); }}
                         data-testid={`tc-invoice-edit-amount-${i}`} className="w-24 h-9 rounded-lg bg-slate-900 border border-white/15 px-2.5 text-xs outline-none focus:border-amber-400" />
                  <button onClick={() => setEditFor({ ...editFor, line_items: editFor.line_items.filter((_, k) => k !== i) })}
                          data-testid={`tc-invoice-edit-delline-${i}`} className="text-red-400/70 hover:text-red-300"><Trash2 size={13} /></button>
                </div>
              ))}
              <button onClick={() => setEditFor({ ...editFor, line_items: [...editFor.line_items, { desc: "", amount: 0 }] })}
                      data-testid="tc-invoice-edit-addline" className="text-[11px] text-cyan-300 font-bold">+ Add line</button>
            </div>
            <div className="flex gap-2 mb-3">
              <input type="date" value={editFor.due_date} onChange={(e) => setEditFor({ ...editFor, due_date: e.target.value })}
                     data-testid="tc-invoice-edit-due" className="h-9 rounded-lg bg-slate-900 border border-white/15 px-2.5 text-xs text-slate-300" />
              <input value={editFor.notes} onChange={(e) => setEditFor({ ...editFor, notes: e.target.value })} placeholder="Notes shown on the invoice"
                     data-testid="tc-invoice-edit-notes" className="flex-1 h-9 rounded-lg bg-slate-900 border border-white/15 px-2.5 text-xs outline-none focus:border-amber-400" />
            </div>
            <div className="flex justify-between items-center">
              <div className="text-xs font-mono text-slate-400">New total: <span className="text-amber-300 font-bold">${editFor.line_items.reduce((s, li) => s + (Number(li.amount) || 0), 0).toLocaleString()}</span></div>
              <div className="flex gap-2">
                <button onClick={() => setEditFor(null)} className="px-4 py-2 rounded-full border border-white/15 text-slate-300 text-xs font-bold">Cancel</button>
                <button onClick={saveEdit} disabled={busy} data-testid="tc-invoice-edit-save"
                        className="px-5 py-2 rounded-full bg-amber-500 text-black font-bold text-xs disabled:opacity-60">
                  {busy ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
